import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames } from '../modules/Constants';

export class VendorExpenseModel extends BaseModel {

    supplementalFormatting(formatting) {

        formatting.columns[4].width = 300;
        formatting.columns[10].width = 300;
        formatting.columns[11].width = 150;
        formatting.columns[12].width = 150;
        formatting.columns[15].width = 150;
        formatting.columns[16].width = 150;
        formatting.columns[17].width = 300;
        formatting.columns[18].width = 150;

        return formatting;
    }

    constructor() {
        super();
        this.TabName = TabNames.VENDOR_EXPENSE;
        this.TabIndex = 3;
        this.DataProcessor = new VendorExpenseProcessor();
    }
}

export class VendorExpenseProcessor {
    ExchangeRates;
    Years;
    Currency;
    Records;
    Headers;

    process(data) {

        this.ExchangeRates = [];
        this.Years = [];
        this.Records = [];
        this.Headers = [];
        this.Currency = data.currency;

        let study = data.study;
        let snapshots = data.snapshots;
        let version = data.version;


        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                this.ExchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })
        
        var yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        this.Years = yearsSpending;


        //JG - Update Years in case there are additional years outside of Spending Years.
        snapshots.forEach(snapshot => {
            snapshot?.purchases?.forEach(purchase => {
                let vendorExpenses = purchase.VendorExpenses;

                vendorExpenses.forEach(vendorExpense => {
                    let costs = vendorExpense?.Value?.Fields[0]?.Cost?.Value?.Fields[0].Values

                    costs.forEach(cost => {
                        let year = new Date(cost.Date).getUTCFullYear();

                        if (this.Years.findIndex(x => x === year) <= -1) {
                            this.Years.push(year);
                        }
                    })
                })
            });
        });


        this.Years.sort();

        snapshots.forEach(snapshot => {
            this.setRecords(snapshot);
        });

        this.configureHeader();
        let result = {
            headers: this.Headers,
            display: this.getDisplay(),
            numerics: this.getNumerics()
        };


        return result;
    }

    setRecords(snapshot) {
        let purchases = snapshot.Purchases;
        let parts = snapshot.SaleableParts;

        //JG: Get all the parts from the Saleable Parts BOM as a flattened list
        let bom = parts.map(part => part.Bom.value.parts.concat(...part.Bom.value.partGroups)).reduce(function (a, b) { return a.concat(b) }, []);

        //JG: Filter the bom's flattened listed to be unique parts (Name & Part Number)
        let bomParts = []
        bom.forEach(function (item) {
            let i = bomParts.findIndex(x => x.name.value === item.name.value && item.partId.partNumber === x.partId.partNumber);
            if (i <= -1) {
                bomParts.push(item);
            }
        });

        bomParts.forEach(bomPart => {
            let bomPartPurchases = purchases.filter(purchase => {
                return purchase.Name.Value.Fields[0] === bomPart.name.value && purchase.Part.Number.Fields[0] === bomPart.partId.partNumber
            });

            if (bomPartPurchases?.length < 1) {
                this.Records.push(new VendorExpenseRowModel(this.Years, bomPart, null, null, this.ExchangeRates, this.Currency))
                return;
            }

            bomPartPurchases.forEach(purchase => {

                if (purchase.VendorExpenses?.length < 1) {
                    this.Records.push(new VendorExpenseRowModel(this.Years, bomPart, purchase, null, this.ExchangeRates, this.Currency));
                    return;
                }

                purchase.VendorExpenses.forEach(vendorExpense => {
                    this.Records.push(new VendorExpenseRowModel(this.Years, bomPart, purchase, vendorExpense, this.ExchangeRates, this.Currency));
                })
            })
        });
    }

    configureHeader() {
        let header = [
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.COMPONENT_PART_NUMBER,
            ColumnHeaders.COMPONENT_PART_REV,
            ColumnHeaders.COMPONENT_PART_SUFFIX,
            ColumnHeaders.COMPONENT_NAME,
            ColumnHeaders.QTY_PER_PARENT,
            ColumnHeaders.QTY_PER_SALEABLE,
            ColumnHeaders.UOM,
            ColumnHeaders.PRODUCTION_METHOD,
            ColumnHeaders.RECV_PLANT,
            ColumnHeaders.PRODCUT_ENGINEER_COMMENTS,
            ColumnHeaders.SUPPLIER_NAME,
            ColumnHeaders.SUPPLIER_LOCATION,
            ColumnHeaders.LOGISTICS_LOCATION,
            ColumnHeaders.PURCHASE_DATA_TYPE,
            ColumnHeaders.SOURCING_RISK,
            ColumnHeaders.REACH_ASSUMPTIONS,
            ColumnHeaders.PURCHASING_COMMENTS,
            ColumnHeaders.VENDOR_EXPENSE_DESCRIPTION
        ]

        this.Years.forEach(year => {
            header.push(year + " Cost");
        });

        header.push(
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        );

        this.Years.forEach(year => {
            header.push(`${year} Cost ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            ColumnHeaders.TOTAL_COST,
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        );

        this.Headers = header;
    }

    getDisplay() {
        let display = [];

        display.push(this.Headers);

        this.Records.forEach(record => {
            let row = [
                record.OptionName,
                record.ComponentPartNumber,
                record.ComponentPartRevision,
                record.ComponentPartSuffix,
                record.ComponentName,
                record.QuantityPerParent,
                record.QuantityPerSaleable,
                record.UoM,
                record.ProductionMethod,
                record.RecvPlant,
                record.ProductEngineerComments,
                record.SupplierName,
                record.SupplierLocation,
                record.LogisticsLocation,
                record.PurchaseDataType,
                record.SourcingRisk,
                record.ReachAssumptions,
                record.PurchasingComments,
                record.VendorExpenseDescription
            ]

            record.Cost.forEach(cost => {
                row.push(cost);
            });

            row.push(
                this.Years[0],
                record.Currency
            )

            record.CostConverted.forEach(cost => {
                row.push(cost);
            });

            row.push(
                record.CostConvertedTotal, //Total Cost (Converted)
                this.Years[0], // Start Year
                this.Currency // Currency (Converted)
            )

            display.push(row);
        });

        return display;
    }

    getNumerics() {
        let numeric = [];

        //set number formatting
        let startCost = 19;
        let endCost = this.Years.length + startCost - 1;
        let startConvCost = endCost + 3;
        let endConvCost = this.Years.length + startConvCost;

        numeric.push({ start: startCost, end: endCost });
        numeric.push({ start: startConvCost, end: endConvCost });

        return numeric;
    }
}

export class VendorExpenseRowModel {
    OptionName = "";
    ComponentPartNumber = "";
    ComponentPartRevision = "";
    ComponentPartSuffix = "";
    ComponentName = "";
    QuantityPerParent = "";
    QuantityPerSaleable = "";
    UoM = "";
    ProductionMethod = "";
    RecvPlant = "";
    ProductEngineerComments = "";
    SupplierName = "";
    SupplierLocation = "";
    LogisticsLocation = "";
    PurchaseDataType = "";
    SourcingRisk = "";
    ReachAssumptions = "";
    PurchasingComments = "";
    VendorExpenseDescription = "";
    Currency = "";
    Cost = [];
    CostConverted = [];
    CostConvertedTotal = 0;

    constructor(years, part, purchase, vendorExpense, exchangeRates, exchangeCurrency) {
        this.OptionName = part.groupName;
        this.ComponentPartNumber = part.partId.partNumber;
        this.ComponentPartRevision = part.partId.drawingRevision;
        this.ComponentPartSuffix = part.partId.suffix;
        this.ComponentName = part.name.value;
        this.QuantityPerParent = part.quantityPerParent.value;
        this.QuantityPerSaleable = part.quantityPerSaleable;
        this.UoM = part.unitOfMeasure?.value;
        this.ProductionMethod = part.productionMethod?.value?.method;
        this.RecvPlant = part.productionMethod?.value?.receivingPlant?.value.plantNumber;
        this.ProductEngineerComments = part.peComments?.value;
        this.SupplierName = purchase?.SupplierName?.Value?.Fields[0];
        this.SupplierLocation = purchase?.SupplierLocation?.Value?.Fields[0];
        this.LogisticsLocation = purchase?.LogisticsLocation?.Value?.Fields[0]?.Fields[0];
        this.PurchaseDataType = purchase?.PurchaseDataType?.Value?.Fields[0]?.Case;
        this.SourcingRisk = purchase?.SourcingRisk?.Value?.Fields[0];
        this.ReachAssumptions = purchase?.ReachAssumptions?.Value?.Fields[0];
        this.PurchasingComments = purchase?.PurchasingComments?.Value?.Fields[0];
        this.VendorExpenseDescription = vendorExpense?.Value?.Fields[0]?.Description?.Value?.Fields[0];
        this.Currency = vendorExpense?.Value?.Fields[0]?.Cost?.Value?.Fields[0]?.Tag;
        this.Cost = [];
        this.CostConverted = [];
        this.CostConvertedTotal = 0;

        years.forEach(year => {
            let cost = undefined;
            let convCost = undefined;

            if (vendorExpense) {
                cost = vendorExpense?.Value?.Fields[0]?.Cost?.Value?.Fields[0].Values.find(item => new Date(item.Date).getUTCFullYear() === year)?.Value ?? 0;

                let exchangeRate = exchangeRates.find(rate => rate.year === year && rate.currency === exchangeCurrency);
                let currentCurrencyRate = exchangeRates.find(rate => rate.year === year && rate.currency === this.Currency);

                if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                    if (cost !== undefined) {
                        this.CostConverted.push("Error");
                    } else {
                        this.CostConverted.push("");
                    }
                } else {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.CostConvertedTotal += convCost;
                }
            }

            this.Cost.push(cost);
            this.CostConverted.push(convCost);
        });
    }

}